import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { fetchCustomerFeatures } from './fetch-customer-features';
import { fetchCustomerPackage } from './fetch-customer-packages';

import handleCustomerFeatures from './helpers/handle-customer-data';
import { setCustomerData } from '@/store/actions';
import { useStore } from '@/store/useStore';
import { APIServiceType } from '@/api/types';

type CustomerDataQueryParams = {
  isSWJGame?: boolean;
  isDYBGame?: boolean;
  isOptedIn?: boolean;
  jackpotId?: string;
};

const useCustomerData = (
  queryParams?: CustomerDataQueryParams,
  enabled = true,
) => {
  const hasActiveSession = useStore(
    (s) => s.userSessionContext.hasActiveSession,
  );
  const gameResetRevision = useStore((s) => s.gameResetRevision);
  const sessionResetRevision = useStore((s) => s.sessionResetRevision);

  const queryKey = useMemo(
    () => ['customer-data', JSON.stringify(queryParams ?? null)] as const,
    [queryParams],
  );

  const query = useQuery({
    queryKey,
    queryFn: () => fetchCustomerFeatures(queryParams ?? {}),
    meta: { apiService: APIServiceType.CUSTOMER_DATA },
    enabled: hasActiveSession && enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!hasActiveSession || !enabled) {
      setCustomerData({ loading: false });
      return;
    }
    const hasData = query.data != null;
    setCustomerData({ loading: query.isFetching && !hasData });
  }, [hasActiveSession, enabled, query.data, query.isFetching]);

  useEffect(() => {
    if (!hasActiveSession || !enabled) {
      return;
    }
    // Customer features are GW shell state (maintenance, flags). With usePrismic GLS
    // does not depend on this payload — refetch into the store without reloading gc.
    void query.refetch();
  }, [
    gameResetRevision,
    sessionResetRevision,
    hasActiveSession,
    enabled,
    query.refetch,
  ]);

  useEffect(() => {
    if (!query.data) {
      return;
    }
    handleCustomerFeatures(query.data);
  }, [query.data]);

  const fetchPackage = () => {
    fetchCustomerPackage();
  };

  return {
    fetchPackage,
  };
};

export default useCustomerData;
